// macro-time — Konvertiert Finnhub-Zeitstempel (UTC) in Berlin + ET.
//
// Finnhub /calendar/economic liefert `time` als `YYYY-MM-DD HH:MM:SS`
// und das ist UTC (laut Finnhub-Docs + Reddit/Github-Issues). Doc sitzt
// in Berlin → ohne Konvertierung muss er im Kopf umrechnen. Trader denken
// zusätzlich in ET (Wall-Street-Standard, Market-Open 9:30 ET).
// Wir zeigen beide nebeneinander.
//
// Frühere Annahme `release_time_et` (das Feld-Suffix) war falsch — die
// Daten sind UTC. Wir fixen das hier transparent, das Backend-Field bleibt
// aus Schema-Stabilität wie es ist.

const TIME_FMT_BERLIN = new Intl.DateTimeFormat('de-DE', {
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
  timeZone: 'Europe/Berlin',
});

const TIME_FMT_ET = new Intl.DateTimeFormat('en-US', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'America/New_York',
});

const DATE_TZ_FMT = new Intl.DateTimeFormat('en-CA', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'Europe/Berlin',
});

export interface MacroTimeDisplay {
  /** Berlin-Zeit „HH:MM" mit Berlin-Datum falls Wechsel (z.B. „00:30 (+1 Tag)"). */
  berlin: string;
  /** ET „HH:MM" — Trader-Standard. */
  et: string;
  /** Kombinierte Anzeige für die Card: „14:30 Berlin · 08:30 ET" */
  combined: string;
}

/**
 * Parsed das Finnhub-Format `time` (z.B. "2026-05-20 18:00:00" oder
 * "2026-05-20T18:00:00Z") als UTC. Doc-Anweisung: keine Erfindung — wenn
 * Input falsches Format, geben wir null zurück damit Caller den Raw-String
 * fallback'en kann.
 */
function parseFinnhubUtc(raw: string | null | undefined, fallbackDate?: string): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Falls die Zeit nur als „HH:MM" oder „HH:MM:SS" kommt UND wir ein
  // Fallback-Datum haben, kombinieren wir.
  const isTimeOnly = /^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed);
  if (isTimeOnly && fallbackDate && /^\d{4}-\d{2}-\d{2}$/.test(fallbackDate)) {
    const iso = `${fallbackDate}T${trimmed.length === 5 ? `${trimmed}:00` : trimmed}Z`;
    const ts = Date.parse(iso);
    return Number.isFinite(ts) ? new Date(ts) : null;
  }

  // Standard-Form „YYYY-MM-DD HH:MM:SS" → ISO mit Z (UTC)
  const isoLike = trimmed.includes('T') ? trimmed : trimmed.replace(' ', 'T');
  const withZ = /[Zz]|[+-]\d{2}:?\d{2}$/.test(isoLike) ? isoLike : `${isoLike}Z`;
  const ts = Date.parse(withZ);
  return Number.isFinite(ts) ? new Date(ts) : null;
}

/**
 * Formatiert Finnhub-UTC-Zeit für die Macro-Page-Anzeige.
 * `raw` = Finnhub `time` Feld, optionales `fallbackDate` falls nur Uhrzeit
 * geliefert wurde.
 */
export function formatMacroTime(
  raw: string | null | undefined,
  fallbackDate?: string,
): MacroTimeDisplay {
  const d = parseFinnhubUtc(raw, fallbackDate);
  if (!d) {
    const placeholder = raw && raw.trim().length > 0 ? raw.trim() : '—';
    return { berlin: placeholder, et: placeholder, combined: placeholder };
  }

  const berlinTime = TIME_FMT_BERLIN.format(d);
  const etTime = TIME_FMT_ET.format(d);

  // Tages-Wechsel-Hinweis: wenn Berlin-Datum != fallbackDate → „(+1 Tag)" o.ä.
  let berlinSuffix = '';
  if (fallbackDate && /^\d{4}-\d{2}-\d{2}$/.test(fallbackDate)) {
    const berlinDate = DATE_TZ_FMT.format(d);
    if (berlinDate !== fallbackDate) {
      const diff = compareDateStrings(berlinDate, fallbackDate);
      if (diff !== 0) {
        berlinSuffix = ` (${diff > 0 ? '+' : ''}${diff} Tag${Math.abs(diff) === 1 ? '' : 'e'})`;
      }
    }
  }

  return {
    berlin: `${berlinTime}${berlinSuffix}`,
    et: etTime,
    combined: `${berlinTime}${berlinSuffix} · ${etTime} ET`,
  };
}

function compareDateStrings(a: string, b: string): number {
  const da = Date.parse(`${a}T00:00:00Z`);
  const db = Date.parse(`${b}T00:00:00Z`);
  return Math.round((da - db) / (24 * 60 * 60 * 1000));
}
